import { useEffect, useRef, useState } from 'react';
import './SingleSelectCriterion.css';
import '../../../Common/typography.css';
import { ReactComponent as ExpandIcon } from '../../../../assets/icons/arrow-down-s-line.svg';
import { AssetTypeItem } from '../../../../types/fundScreener.type';

// Shared by Asset Type and Classification filters — same shape and
// single-select behavior, only the "all" label and dropdown scrollability differ.
export interface SingleSelectCriterionProps {
  allLabel: string;
  items: AssetTypeItem[];
  setItems: (items: AssetTypeItem[]) => void;
  variant?: 'dropdown' | 'list';
  scrollable?: boolean;
}

const SingleSelectCriterionList = ({
  items,
  setItems,
}: Pick<SingleSelectCriterionProps, 'items' | 'setItems'>) => {
  const selectValue = (value: string) => {
    setItems(items.map((item) => ({ ...item, activated: item.value === value })));
  };

  return (
    <div className="fund-single-select-options">
      {items.map((item) => (
        <div
          key={item.value}
          className={`fund-single-select-option-item body-m-regular ${item.activated ? 'active' : ''}`}
          role="option"
          aria-selected={item.activated}
          onClick={() => selectValue(item.value)}
          tabIndex={0}
        >
          {item.label}
        </div>
      ))}
    </div>
  );
};

const SingleSelectCriterion = ({
  allLabel,
  items,
  setItems,
  variant = 'dropdown',
  scrollable = false,
}: SingleSelectCriterionProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (variant === 'list') {
    return <SingleSelectCriterionList items={items} setItems={setItems} />;
  }

  const activeItem = items.find((item) => item.activated);
  const buttonLabel = activeItem && activeItem.value !== 'ALL' ? activeItem.label : allLabel;

  return (
    <div ref={dropdownRef} className="fund-single-select-criterion">
      <button
        type="button"
        className="fund-single-select-button body-m-regular"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="fund-single-select-button-label">{buttonLabel}</span>
        <ExpandIcon className={`fund-single-select-expand ${isOpen ? 'open' : 'closed'}`} />
      </button>

      {isOpen && (
        <div className={`fund-single-select-dropdown ${scrollable ? 'scrollable' : ''}`}>
          <SingleSelectCriterionList
            items={items}
            setItems={(next) => {
              setItems(next);
              setIsOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SingleSelectCriterion;
